import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// One load of the item catalogue per page visit, shared by every picker on the page.
let catalogue = null;
export function loadCatalogue(force = false) {
  if (force || !catalogue) catalogue = api.get("/inventory/items").then(({ data }) => (data || []).filter((i) => i.is_active !== false)).catch(() => []);
  return catalogue;
}

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/**
 * Description box with a dropdown of catalogue items. Typing filters the list; picking an item
 * calls onPick(item) so the form can fill description, unit and price (the price stays editable).
 * You can still type any description that is not in the catalogue.
 * prefer: "SERVICE" lists service items first (work orders), otherwise goods first.
 */
export function ItemPicker({ value, onChange, onPick, prefer, required, className, placeholder = "Type or pick an item", ...rest }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);

  useEffect(() => { loadCatalogue().then(setItems); }, []);
  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const matches = useMemo(() => {
    const q = String(value || "").toLowerCase().trim();
    const list = items.filter((i) => !q || [i.code, i.name, i.description].some((t) => String(t || "").toLowerCase().includes(q)));
    const rank = (i) => (prefer === "SERVICE" ? (i.category === "SERVICE" ? 0 : 1) : (i.category === "SERVICE" ? 1 : 0));
    return list.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name)).slice(0, 30);
  }, [items, value, prefer]);

  const pick = (item) => {
    onPick?.(item);
    setOpen(false);
  };

  return (
    <div ref={box} className={cn("relative min-w-0 flex-1", className)}>
      <Input
        required={required}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={(e) => {
          if (!open || !matches.length) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === "Enter") { e.preventDefault(); pick(matches[active]); }
          if (e.key === "Escape") setOpen(false);
        }}
        {...rest}
      />
      {open && matches.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
          {matches.map((i, idx) => (
            <button
              type="button"
              key={i.id}
              onMouseDown={(e) => { e.preventDefault(); pick(i); }}
              onMouseEnter={() => setActive(idx)}
              className={cn("flex w-full items-start justify-between gap-3 rounded px-2 py-1.5 text-left text-sm", idx === active ? "bg-accent" : "")}
            >
              <span className="min-w-0">
                <span className="block truncate text-foreground">{i.name}</span>
                <span className="block truncate text-[11px] text-muted-foreground">{i.code}{i.description ? ` · ${i.description}` : ""}</span>
              </span>
              <span className="shrink-0 text-right font-mono text-xs text-foreground">
                {Number(i.standard_rate) > 0 ? inr(i.standard_rate) : ""}
                <span className="block text-[10px] font-sans text-muted-foreground">{i.category === "SERVICE" ? "Service" : i.category === "NON_STOCK" ? "Non-stock" : "Stock"}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
